import type { CityConfig } from "@/pages/city-template/types";

export const tigardConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Oregon Vital Records (Birth, Death, Marriage)", desc: "Request certified birth, death, and marriage certificates from Oregon Vital Records.", url: "https://www.oregon.gov/oha/PH/BirthDeathCertificates/Pages/index.aspx" },
    { icon: "ri-government-line", label: "Washington County Clerk Vital Records", desc: "Obtain vital records through the Washington County Clerk's office.", url: "https://www.washingtoncountyor.gov/CountyClerk" },
    { icon: "ri-global-line", label: "VitalChek — Order Vital Records Online", desc: "Official online ordering partner of Oregon Vital Records for certified certificates.", url: "https://www.vitalchek.com/" },
  ],
  city: "Tigard",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "tigard",
  route: "/tigard",
  title: "Emergency Passport Tigard, OR | Expedited Passport Renewal & Rush Services",
  metaDescription: "Emergency passport and expedited passport services in Tigard, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Tigard and Washington County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Tigard, Oregon",
  heroSubtitle: "Flying out of PDX for a family vacation or business trip? Passport expired or lost? Fast Passport Center helps Tigard travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Tigard%20Oregon%20suburban%20neighborhood%20with%20modern%20family%20homes%20and%20tree-lined%20streets%2C%20green%20Pacific%20Northwest%20trees%2C%20clear%20blue%20sky%2C%20well-maintained%20community%20feel%2C%20warm%20natural%20daylight%2C%20photorealistic%20suburban%20landscape%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=tigard-hero-01&orientation=landscape",
  heroImageAlt: "Tigard Oregon suburban neighborhood with modern homes and green trees",
  geoPosition: "45.4312;-122.7715",
  geoPlacename: "Tigard, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Tualatin", zips: "97062" },
    { name: "Sherwood", zips: "97140" },
    { name: "King City", zips: "97224" },
    { name: "Beaverton", zips: "97002–97078" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "Wilsonville", zips: "97070" },
    { name: "Newberg", zips: "97132" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Aloha", zips: "97003, 97006, 97007, 97008" },
    { name: "Garden Home", zips: "97223" },
    { name: "Raleigh Hills", zips: "97225" },
    { name: "Cedar Hills", zips: "97225" },
    { name: "Bull Mountain", zips: "97224" },
    { name: "Metzger", zips: "97223" },
    { name: "Durham", zips: "97224" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in Tigard in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from Tigard", desc: "FedEx and UPS locations throughout Tigard and Tualatin offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute passport needs." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "James L.",
      location: "Tigard, OR",
      rating: 5,
      text: "Family trip to South Korea to visit relatives and my passport was three months expired. Fast Passport Center got it renewed in 5 days. I shipped from the UPS on Pacific Highway. Couldn't believe how smooth it was.",
      service: "Passport Renewal",
    },
    {
      name: "Kim H.",
      location: "Tualatin, OR",
      rating: 5,
      text: "My son's passport was lost right before our trip to Japan. Fast Passport Center guided us through the DS-64 and replacement process. We had the new passport in 6 days. The step-by-step instructions were a lifesaver.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Amanda R.",
      location: "Sherwood, OR",
      rating: 5,
      text: "Corporate retreat to Cancun in 10 days and my passport was damaged from a washing machine incident. Fast Passport Center handled the damaged passport replacement and I had it back in 4 days. Truly grateful.",
      service: "Damaged Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "tigard-post",
      name: "Tigard Post Office",
      type: "USPS Passport Acceptance",
      address: "11930 SW Greenburg Rd",
      city: "Tigard",
      state: "OR",
      zip: "97223",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Central Tigard",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Convenient passport acceptance in central Tigard. Appointment required — book online well in advance. Photo services may be available.",
      icon: "ri-mail-line",
    },
    {
      id: "tualatin-post",
      name: "Tualatin Post Office",
      type: "USPS Passport Acceptance",
      address: "19235 SW Martinazzi Ave",
      city: "Tualatin",
      state: "OR",
      zip: "97062",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Tualatin — about 3 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Tualatin option for southern Tigard and Washington County residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "beaverton-post",
      name: "Beaverton Post Office",
      type: "USPS Passport Acceptance",
      address: "4265 SW 106th Ave",
      city: "Beaverton",
      state: "OR",
      zip: "97005",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Beaverton — about 5 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Beaverton option for northern Tigard residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "washington-county",
      name: "Washington County Clerk",
      type: "County Passport Acceptance",
      address: "155 N 1st Ave, Room 130",
      city: "Hillsboro",
      state: "OR",
      zip: "97124",
      phone: "503-846-8791",
      hours: "Mon–Fri 8am–5pm (by appointment)",
      distance: "Hillsboro — about 8 miles west",
      website: "https://www.washingtoncountyor.gov/CountyClerk",
      websiteLabel: "Washington County Clerk",
      notes: "County clerk passport acceptance for Washington County residents. Often faster scheduling than USPS. Appointment required.",
      icon: "ri-government-line",
    },
    {
      id: "sherwood-post",
      name: "Sherwood Post Office",
      type: "USPS Passport Acceptance",
      address: "16335 SW Langer Dr",
      city: "Sherwood",
      state: "OR",
      zip: "97140",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Sherwood — about 6 miles southwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Sherwood option for southwestern Tigard area. Confirm appointment and photo availability.",
      icon: "ri-mail-line",
    },
    {
      id: "portland-pearl",
      name: "Portland Main Post Office — Pearl District",
      type: "USPS Passport Acceptance",
      address: "715 NW Hoyt St",
      city: "Portland",
      state: "OR",
      zip: "97208",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm, Sat 9am–2pm",
      distance: "Portland — about 10 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Major Portland passport acceptance facility. Full service including photos. Appointment required.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Tigard before international travel?",
      a: "Yes. Through Fast Passport Center, Tigard travelers can get an expedited passport in as little as 2–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — don't wait until the last minute.",
    },
    {
      q: "How fast can I renew my passport in Tigard?",
      a: "Fast passport renewal in Tigard is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my family trip to Asia?",
      a: "If your passport expires before your family trip to Asia, act immediately. Many Asian countries require your passport to be valid for at least 6 months beyond your arrival date. Fast Passport Center can expedite your passport renewal so you don't miss your flight. The sooner you start, the more options you have.",
    },
    {
      q: "Can I get a same-day passport appointment near Tigard?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Tigard, Fast Passport Center is the faster and more predictable option — no Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in Tigard?",
      a: "Yes. Child passport expediting in Tigard is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps and lost passports cannot be used even if found later.",
    },
    {
      q: "Does Tigard have a passport agency?",
      a: "Tigard, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north) and the San Francisco Passport Agency in California (about 640 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Tigard residents.",
    },
    {
      q: "How long does a standard passport take in Tigard?",
      a: "If you apply through a standard post office acceptance facility in Tigard or nearby Portland, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Tigard travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in Tigard without visiting an office?",
      a: "Yes. Most Tigard-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in the Portland metro area.",
    },
    {
      q: "What airports do Tigard passport holders typically fly from?",
      a: "Portland International Airport (PDX) is the primary airport for Tigard travelers, located about 12 miles northeast. PDX offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. Salem (SLE) and Eugene (EUG) are regional alternatives. If your passport isn't in hand, don't book international travel. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Tigard", address: "16275 SW Pacific Hwy, Tigard, OR 97224", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Tualatin", address: "19251 SW Martinazzi Ave, Tualatin, OR 97062", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Tigard", address: "11527 SW Pacific Hwy, Tigard, OR 97223", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "The UPS Store — Beaverton", address: "1590 NW 185th Ave, Beaverton, OR 97006", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walgreens — Beaverton", address: "4755 SW 158th Ave, Beaverton, OR 97007", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Tigard", address: "7600 SW Dartmouth St, Tigard, OR 97223", hours: "Daily 7 AM – 11 PM" },
    { name: "Costco — Tigard", address: "7855 SW Dartmouth St, Tigard, OR 97223", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "CVS — Portland", address: "908 SW 4th Ave, Portland, OR 97204", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Portland", address: "810 SW Broadway, Portland, OR 97205", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Walgreens — Sherwood", address: "16335 SW Langer Dr, Sherwood, OR 97140", hours: "Daily 8 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Tigard DMV Office",
      address: "14799 SE 82nd Dr, Clackamas, OR 97015",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Nearest DMV for Tigard residents. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Beaverton DMV Office",
      address: "14075 SW Barrows Rd, Beaverton, OR 97007",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Washington County option for western Tigard residents.",
      featured: false,
    },
    {
      name: "Hillsboro DMV Office",
      address: "5330 NE Elam Young Pkwy, Hillsboro, OR 97124",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Northwest Washington County option. Appointment recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 12 miles northeast",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. The busiest travel seasons are May–August and December–January — plan passport needs well in advance.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 175 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Tigard travelers use SEA for additional flight options. Passport required for all international departures.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PDX in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Tigard and the Washington County area.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Tigard and Washington County travelers renew fast — passport expired before trip is one of our most common urgent requests.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Tigard", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Tigard travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Tigard families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Tigard residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for Tigard travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Tigard, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Tigard residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Tigard, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Tigard travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Tigard families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Tigard, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Tigard travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip to Asia or Europe, especially for summer and holiday travel from PDX." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in Tigard." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Tigard runs 6–13 weeks. If your trip to Asia, Europe, or Mexico is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Tigard travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Tigard", description: "Drop off at any FedEx or UPS in the Tigard or Portland metro area — most locations are open evenings and weekends. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from Tigard" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Portland", route: "/portland" },
    { name: "Fast passport renewal in Lake Oswego", route: "/lake-oswego" },
    { name: "Emergency passport help near Tualatin", route: "/tualatin" },
    { name: "Rush passport service in Beaverton", route: "/beaverton" },
    { name: "Fast passport renewal for Washington County travelers", route: "/oregon" },
  ],
  fpcOfficeAddress: "5608 17th Avenue NW, Seattle, WA 98107",
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Tigard or the Portland metro area — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Tigard and all of Washington County remotely",
  serviceAreaIntro: "Whether you are in Tigard, Tualatin, Sherwood, Beaverton, or anywhere in Washington County, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Tigard and Washington County zip codes. Ship overnight from any FedEx or UPS in the Portland metro area and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Tigard as simple as possible. Most Tigard clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Tigard travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from Tigard, Tualatin, Sherwood, and Washington County travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Tigard and Washington County travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  dmvWebsite: "https://www.oregon.gov/odot/dmv",
  dmvLabel: "Oregon DMV — Official Website",
};